import { IonButton, IonCard, IonCardContent, IonIcon, IonRange, RangeKnobMoveEndEventDetail } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { DqText } from '../dq-text';
import { play, playForward, refresh, pause } from 'ionicons/icons';
import './JobTimeRangeCard.scss';
import { msToTime } from 'src/utils/logs/logHelpers';
import { FormattedMessage, useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';

interface JobTimeRangeCardProps {
  activeRunTime: number;
  onChange: (min: number, max: number) => void;
  showTimeDetails: boolean;
}

const JobTimeRangeCard: React.FC<JobTimeRangeCardProps> = props => {
  const { formatNumber } = useIntl();
  const { onChange } = props;
  const [lower, setLower] = useState<number>(0);
  const [upper, setUpper] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const tick = useRef<number>();
  const fastForwardMulti = 0.1;
  const playMulti = 0.05;

  // An effect that automatically resets the upper bound of the time range to the
  // total time of the job log, each time the total time changes.
  useEffect(() => {
    setUpper(props.activeRunTime);
  }, [props.activeRunTime]);

  const handleRangeChange = React.useCallback((event: CustomEvent<RangeKnobMoveEndEventDetail>) => {
    if (typeof event.detail.value === 'number') {
      console.warn('Invalid event received from IonRange:', event);
      return;
    }

    const min = event.detail.value.lower;
    const max = event.detail.value.upper;
    setLower(min);
    setUpper(max);
  }, []);

  const resetRange = () => {
    handleRangeChange(
      new CustomEvent('', {
        detail: {
          value: {
            lower: 0,
            upper: props.activeRunTime,
          },
        },
      }),
    );
    setIsPlaying(false);
  };

  const handleFastForward = () => {
    // Add a 10th of the max to the upper time
    const tempTime = upper + props.activeRunTime * fastForwardMulti;
    const upperVal = tempTime > props.activeRunTime ? props.activeRunTime : tempTime;
    handleRangeChange(
      new CustomEvent('', {
        detail: {
          value: {
            lower,
            upper: upperVal,
          },
        },
      }),
    );
  };

  // An effect that manages the interval on which the upper limit is adjusted while playing.
  useEffect(() => {
    if (isPlaying) {
      tick.current = window.setInterval(() => {
        setUpper(up => {
          const tempTime = up + props.activeRunTime * playMulti;
          const upperVal = tempTime > props.activeRunTime ? props.activeRunTime : tempTime;
          return upperVal;
        });
      }, 1000);
    }

    return () => clearInterval(tick.current);
  }, [isPlaying, props.activeRunTime]);

  // An effect that handles stopping playback when the upper value matches the total elapsed time
  useEffect(() => {
    if (upper === props.activeRunTime) {
      setIsPlaying(false);
    }
  }, [props.activeRunTime, upper]);

  // An effect that informs the parent that the time range bounds have changed, each time a change occurs
  useEffect(() => {
    onChange(lower, upper);
  }, [lower, onChange, upper]);

  const toggleStart = () => {
    setIsPlaying(!isPlaying);
  };

  const startPlay = () => {
    setIsPlaying(true);
    handleRangeChange(
      new CustomEvent('', {
        detail: {
          value: {
            lower: 0,
            upper: 0,
          },
        },
      }),
    );
  };

  return (
    <IonCard className="no-print">
      <IonCardContent className="job-time-range-content">
        <DqText variant="h3">
          <FormattedMessage {...JobMessages.jobTimeRangeTitle} />
        </DqText>
        <IonRange
          dualKnobs={true}
          min={0}
          max={props.activeRunTime}
          value={{
            lower,
            upper,
          }}
          onIonKnobMoveEnd={event => handleRangeChange(event as CustomEvent)}
        />
        <div className="job-time-range-buttons">
          {!isPlaying ? (
            upper === props.activeRunTime ? (
              // Start play
              <IonButton
                onClick={() => {
                  startPlay();
                }}
              >
                <IonIcon slot="icon-only" color="dark" size="large" icon={play} />
              </IonButton>
            ) : (
              // Continue playing
              <IonButton
                onClick={() => {
                  toggleStart();
                }}
              >
                <IonIcon slot="icon-only" color="dark" size="large" icon={play} />
              </IonButton>
            )
          ) : (
            // Pause
            <IonButton
              onClick={() => {
                toggleStart();
              }}
            >
              <IonIcon slot="icon-only" color="dark" size="large" icon={pause} />
            </IonButton>
          )}
          <IonButton
            onClick={() => {
              handleFastForward();
            }}
            disabled={upper === props.activeRunTime}
          >
            <IonIcon slot="icon-only" color="dark" size="large" icon={playForward} />
          </IonButton>
          <IonButton
            onClick={() => {
              resetRange();
            }}
            disabled={upper === props.activeRunTime && lower === 0}
          >
            <IonIcon slot="icon-only" color="dark" size="large" icon={refresh} />
          </IonButton>
        </div>
        {props.showTimeDetails && (
          <DqText variant="body-strong">
            {msToTime(lower, formatNumber) + ' -> ' + msToTime(upper, formatNumber)}
          </DqText>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default JobTimeRangeCard;
